import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpError } from 'app/helpers/httpError';
import { HttpRequestHelper } from 'app/helpers/httpRequestHelper';
import { ExposedCreateMortgageEligibilityInfoPropertiesReq } from 'app/interfaces/requests/Exposed/exposedCreateMortgageEligibilityInfoPropertiesReq';
import { ExposedCreateMortgageEligibilityInfoReq } from 'app/interfaces/requests/Exposed/exposedCreateMortgageEligibilityInfoReq';
import { ExposedSearchAddressReq } from 'app/interfaces/requests/Exposed/exposedSearchAddressReq';
import { ExposedUpdateMortgageEligibilityInfoReq } from 'app/interfaces/requests/Exposed/exposedUpdateMortgageEligibilityInfoReq';
import { ExposedUpdateMortgageEligibilityPropertyInfoReq } from 'app/interfaces/requests/Exposed/exposedUpdateMortgageEligibilityPropertyInfoReq';
import { ExposedListMortgageEligibilityInfoRes } from 'app/interfaces/responses/Exposed/exposedListMortgageEligibilityInfoRes';
import { ExposedListSearchAddressRes } from 'app/interfaces/responses/Exposed/exposedListSearchAddressRes';
import { ExposedReadDocumentRes } from 'app/interfaces/responses/Exposed/exposedReadDocumentRes';
import { ExposedReadMortgageEligibilityConfigRes } from 'app/interfaces/responses/Exposed/exposedReadMortgageEligibilityConfigRes';
import { ExposedReadMortgageEligibilityInfoExistsRes } from 'app/interfaces/responses/Exposed/exposedReadMortgageEligibilityInfoExistsRes';
import {
    ExposedMortgageEligibilityInfoOriginatingSystemComparisonRes,
    ExposedReadMortgageEligibilityInfoRes,
    ExposedRefreshMortgageEligibilityInfoFromOriginatingSystemRes,
} from 'app/interfaces/responses/Exposed/exposedReadMortgageEligibilityInfoRes';
import { Option } from 'app/interfaces/support/option';
import { HttpStatus } from 'app/interfaces/support/status/httpStatus';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { environment } from 'environments/environment';
import { EMPTY, Observable, Subject, catchError, defer, finalize, map } from 'rxjs';
import { TranslationService } from '../translation/translation.service';

@Injectable({
    providedIn: 'root',
})
export class MortgageEligibilityInfoService {
    private httpRequestHelper = new HttpRequestHelper();

    constructor(
        private http: HttpClient,
        private loaderService: LoaderService,
        private alertsService: AlertsService,
        private translationService: TranslationService,
        private router: Router
    ) {}

    public getMortgageEligibilityConfig(): Observable<ExposedReadMortgageEligibilityConfigRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki upplýsingar');

        let subject = new Subject<ExposedReadMortgageEligibilityConfigRes>();

        this.http
            .get(environment.localApi + 'mortgageEligibilityInfo/mortgageEligibilityConfig')
            .subscribe({
                next: (res: HttpStatus<ExposedReadMortgageEligibilityConfigRes>) => {
                    subject.next(res.returnItem);
                },
                error: (error) => HttpError(this.alertsService, this.router, error, '', '', '', ''),
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public getMortgageEligibilityInfo(id: number): Observable<ExposedReadMortgageEligibilityInfoRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki veðhæfisskjal');

        let subject = new Subject<ExposedReadMortgageEligibilityInfoRes>();

        let options = this.httpRequestHelper.getRequestOptionsAuth();

        this.http
            .get(environment.localApi + 'mortgageEligibilityInfo/' + id, options)
            .subscribe({
                next: (res: HttpStatus<ExposedReadMortgageEligibilityInfoRes>) => {
                    subject.next(res.returnItem);
                },
                error: (error) => HttpError(this.alertsService, this.router, error, '', '', '', ''),
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public getMortgageEligibilityInfos(): Observable<ExposedListMortgageEligibilityInfoRes[]> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki veðhæfisskjöl');

        let subject = new Subject<ExposedListMortgageEligibilityInfoRes[]>();

        this.http
            .get(environment.localApi + 'mortgageEligibilityInfo')
            .subscribe({
                next: (res: HttpStatus<ExposedListMortgageEligibilityInfoRes[]>) => {
                    subject.next(res.returnItem);
                },
                error: (error) => HttpError(this.alertsService, this.router, error, '', '', '', ''),
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public deleteMortgageEligibilityInfo(id: number): Observable<void> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Eyði veðhæfisskjali');

        let subject = new Subject<void>();

        this.http
            .delete(environment.localApi + 'mortgageEligibilityInfo/' + id)
            .subscribe({
                next: () => {
                    subject.next();
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });

        return subject.asObservable();
    }

    public createMortgageEligibilityInfo(req: ExposedCreateMortgageEligibilityInfoReq): Observable<null> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Vista skjal');

        let subject = new Subject<null>();
        this.http
            .post(environment.localApi + 'mortgageEligibilityInfo', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe({
                next: (res: HttpStatus<null>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Skjal hefur verið vistað');
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(res.returnItem);
                },
                error: (error) => HttpError(this.alertsService, this.router, error, '', '', '', ''),
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public updateMortgageElgibilityInfoFromCopy(targetId: number, sourceId: number): Promise<void> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('label_updating_document'));

        return new Promise<void>((resolve, reject) => {
            this.http
                .put(
                    `${environment.localApi}mortgageEligibilityInfo/${targetId}/copy-from/${sourceId}`,
                    null,
                    this.httpRequestHelper.getRequestOptionsAuth()
                )
                .subscribe({
                    next: (res: HttpStatus<null>) => {
                        this.alertsService.changeAlertShowIcon(true);
                        this.alertsService.changeAlertSuccessMessage('Skjal hefur verið vistað');
                        this.alertsService.changeAlertSuccessShow(true);
                        resolve();
                    },
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject(error);
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public updateMortgageEligibilityInfo(req: ExposedUpdateMortgageEligibilityInfoReq): Observable<null> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('label_updating_document'));

        let subject = new Subject<null>();
        this.http
            .post(
                environment.localApi + 'mortgageEligibilityInfo/updateMortgageEligibilityInfo',
                req,
                this.httpRequestHelper.getRequestOptionsAuth()
            )
            .subscribe({
                next: (res: HttpStatus<null>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Skjal hefur verið vistað');
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(res.returnItem);
                    subject.complete();
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.complete();
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public getMortgageEligibilityInfoPropertyDocument(
        mortgageEligibilityInfoId: number,
        propertyId: number,
        documentId: number
    ): Observable<ExposedReadDocumentRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('label_fetching_document'));

        let subject = new Subject<null>();
        this.http
            .get(
                environment.localApi +
                    `mortgageEligibilityInfo/${mortgageEligibilityInfoId}/properties/${propertyId}/document/${documentId}`,
                this.httpRequestHelper.getRequestOptionsAuth()
            )
            .subscribe({
                next: (res: HttpStatus<null>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(this.translationService.translate('label_fetched_document'));
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(res.returnItem);
                    subject.complete();
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.complete();
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public searchAddress(searchString: string): Observable<ExposedListSearchAddressRes[]> {
        let req = {
            searchString,
        } as ExposedSearchAddressReq;

        let subject = new Subject<ExposedListSearchAddressRes[]>();
        this.http
            .post(environment.localApi + 'mortgageEligibilityInfo/searchAddress', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe({
                next: (res: HttpStatus<ExposedListSearchAddressRes[]>) => {
                    subject.next(res.returnItem);
                },
                error: (error) => {
                    subject.error(error);
                },
            });
        return subject.asObservable();
    }

    public getMortgageEligibilityInfoOriginatingSystems(): Observable<Option<string>[]> {
        let subject = new Subject<Option<string>[]>();
        this.http.get(environment.localApi + 'mortgageEligibilityInfo/originatingSystems').subscribe({
            next: (res: HttpStatus<Option<string>[]>) => {
                subject.next(res.returnItem);
            },
            error: (error) => {
                subject.error(error);
            },
        });
        return subject.asObservable();
    }

    public updateMortgageEligibilityInfoPropertyInfoWithRegistryData(
        mortgageEligibilityInfoId: number,
        propertyId: number,
        req: ExposedUpdateMortgageEligibilityPropertyInfoReq
    ): Observable<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('label_updating_document'));

        let subject = new Subject<number>();

        this.http
            .post(
                environment.localApi +
                    `mortgageEligibilityInfo/${mortgageEligibilityInfoId}/properties/${propertyId}/fetchPropertyRegistryData`,
                req,
                this.httpRequestHelper.getRequestOptionsAuth()
            )
            .subscribe({
                next: (res: HttpStatus<number>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(
                        this.translationService.translate('info_mortgage_eligibility_info_updated')
                    );
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(res.returnItem);
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public createMortgageEligibilityInfoProperty(req: ExposedCreateMortgageEligibilityInfoPropertiesReq) {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('label_updating_document'));

        let subject = new Subject<number>();

        this.http
            .post(
                environment.localApi + `mortgageEligibilityInfo/${req.mortgageEligibilityInfoId}/properties`,
                req,
                this.httpRequestHelper.getRequestOptionsAuth()
            )
            .subscribe({
                next: (res: HttpStatus<number>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(
                        this.translationService.translate('info_mortgage_eligibility_info_updated')
                    );
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(res.returnItem);
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public deleteMortgageEligibilityInfoProperty(mortgageEligibilityInfoId: number, propertyId: number) {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('label_updating_document'));

        let subject = new Subject<number>();

        this.http
            .delete(
                environment.localApi + `mortgageEligibilityInfo/${mortgageEligibilityInfoId}/properties/${propertyId}`,
                this.httpRequestHelper.getRequestOptionsAuth()
            )
            .subscribe({
                next: (res: HttpStatus<number>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(
                        this.translationService.translate('info_mortgage_eligibility_info_updated')
                    );
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(res.returnItem);
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public getMortgageEligibilityInfoExists(searchString: string): Promise<ExposedReadMortgageEligibilityInfoExistsRes> {
        return new Promise<ExposedReadMortgageEligibilityInfoExistsRes>((resolve, reject) => {
            this.http.get(environment.localApi + `mortgageEligibilityInfo/exists`, { params: { searchString } }).subscribe({
                next: (res: HttpStatus<ExposedReadMortgageEligibilityInfoExistsRes>) => {
                    resolve(res.returnItem);
                },
                error: (error) => {
                    reject(error);
                },
            });
        });
    }

    public refreshMortgageEligibilityInfoFromOriginatingSystem(
        id: number
    ): Observable<ExposedRefreshMortgageEligibilityInfoFromOriginatingSystemRes> {
        const request = this.http.post<HttpStatus<ExposedRefreshMortgageEligibilityInfoFromOriginatingSystemRes>>(
            environment.localApi + `mortgageEligibilityInfo/${id}/refresh-from-originating-system`,
            {},
            this.httpRequestHelper.getRequestOptionsAuth()
        );

        return this.handleOriginatingSystemRequest(request);
    }

    public getOriginatingSystemComparison(id: number): Observable<ExposedMortgageEligibilityInfoOriginatingSystemComparisonRes> {
        const request = this.http.get<HttpStatus<ExposedMortgageEligibilityInfoOriginatingSystemComparisonRes>>(
            environment.localApi + `mortgageEligibilityInfo/${id}/originating-system-comparison`,
            this.httpRequestHelper.getRequestOptionsAuth()
        );

        return this.handleOriginatingSystemRequest(request);
    }

    private handleOriginatingSystemRequest<T>(request: Observable<HttpStatus<T>>): Observable<T> {
        return defer(() => {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage(this.translationService.translate('label_fetching_document'));

            return request.pipe(
                map((res) => {
                    if (res.returnItem == null) {
                        throw new Error('Mortgage eligibility API returned no data');
                    }

                    return res.returnItem;
                }),
                catchError((error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.changeActiveLoaders(-1);
                })
            );
        });
    }
}
